import React from "react";
import { ApiResponse } from "../../api/types/api-response";
import { ArrowRight, ChevronDown } from "lucide-react";
import { prepareComment } from "../../utils/prepare-comment";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Kbd } from "@/components/ui/kbd";
import { isMac } from "../../utils/use-keyboard-shortcuts";
import { Button } from "@/components/ui/button";

export const ResponseCommentTextarea: React.FC<{
  response: ApiResponse;
  onGetSummary: () => void;
  summary: string;
  isSummaryLoading: boolean;
  onSkip: () => void;
}> = (props) => {
  const [isSummaryVisible, setIsSummaryVisible] = React.useState(false);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false);
  const commentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsSummaryVisible(false);
  }, [props.response.id]);

  // Check if content overflows and track scroll position
  React.useEffect(() => {
    const checkOverflow = () => {
      if (commentRef.current) {
        const hasVerticalOverflow = commentRef.current.scrollHeight > commentRef.current.clientHeight;
        setHasOverflow(hasVerticalOverflow);

        // Check if scrolled to bottom
        const isAtBottom = commentRef.current.scrollHeight - commentRef.current.scrollTop <= commentRef.current.clientHeight + 5;
        setIsScrolledToBottom(isAtBottom);
      }
    };

    checkOverflow();

    const handleResize = () => checkOverflow();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [props.response.id, isSummaryVisible, props.summary]);

  const handleScroll = () => {
    if (commentRef.current) {
      const isAtBottom = commentRef.current.scrollHeight - commentRef.current.scrollTop <= commentRef.current.clientHeight + 5;
      setIsScrolledToBottom(isAtBottom);
    }
  };

  const handleStreamResponse = () => {
    if (!props.summary) {
      props.onGetSummary();
    }

    setIsSummaryVisible(!isSummaryVisible);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Top Controls */}
      <div className="flex gap-2 justify-between items-center flex-wrap mb-1">
        <div className="flex gap-2 flex-wrap text-sm text-muted-foreground min-w-0 flex-1">
          <span>{formatDate(props.response.attributes.created_at)}</span>
          <span>•</span>
          <span>{props.response.attributes.data_type}</span>
          <span>•</span>
          <span>{props.response.attributes.dataset_variant}</span>
          {props.response.attributes.score && (
            <>
              <span>•</span>
              <span>
                Score: {props.response.attributes.score}/{props.response.attributes.max_score}
              </span>
            </>
          )}
          {props.response.attributes.question && (
            <>
              <span>•</span>
              <span className="truncate" title={props.response.attributes.question}>
                {props.response.attributes.question}
              </span>
            </>
          )}
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={props.onSkip}
                className="h-8 w-8"
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex items-center gap-1">
                <Kbd>{isMac() ? "⌘" : "Ctrl"}</Kbd>
                <Kbd>→</Kbd>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Comment Display */}
      <div className="relative">
        <div
          ref={commentRef}
          onScroll={handleScroll}
          className="max-h-[160px] md:max-h-[220px] lg:max-h-[260px] overflow-auto p-3 bg-card text-card-foreground rounded-md border border-border text-base leading-relaxed whitespace-pre-wrap break-words"
        >
          {isSummaryVisible ? (
            <p className={props.isSummaryLoading ? "italic text-muted-foreground" : ""}>
              {props.isSummaryLoading ? "Loading summary..." : props.summary}
            </p>
          ) : (
            <>
              {props.response.attributes.dataset_variant === "support" ? (
                <div>
                  {prepareComment(props.response.attributes.comment).map((item) => item)}
                </div>
              ) : (
                <p>{props.response.attributes.comment}</p>
              )}
            </>
          )}
        </div>

        {/* Scroll Indicator - only show when content overflows and not scrolled to bottom */}
        {hasOverflow && !isScrolledToBottom && (
          <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none flex items-end justify-center pb-2 bg-gradient-to-t from-card via-card/80 to-transparent rounded-b-md">
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-card/90 px-2 py-1 rounded-full border border-border/50 shadow-sm animate-pulse">
              <ChevronDown className="h-3 w-3" />
              <span>Scroll to see more</span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      {props.response.attributes.dataset_variant === "support" && (
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handleStreamResponse}
          >
            {isSummaryVisible ? "Show full dialogue" : "Show summary"}
          </Button>
        </div>
      )}
    </div>
  );
};
